import MaterialReactTable from 'material-react-table'
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import { useState, useEffect, useMemo } from "react";
import {Button, Box, Typography} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded';
import Swal from 'sweetalert2';

function ReactTablePopUpSubmitWrapper({
    title,
    columns,
    data,
    onClose,
    onRowSubmit,
    onRowClose,
    onMultiSubmit,
    onRowAction,
    onCSVExport,
    onPDFExport,
    enableExportToolbar = false,
    isLoading = true,
    enableDetailPanel = false,
    enableMultiSelect = false,
    selectionGroupField = null,
    selectionGroupLabel = 'order number',
    initialPageSize = 15,
    compactRows = true,
}) {

    const [tableColumns, setTableColumns] = useState([]);
    const [rowSelection, setRowSelection] = useState({});

    useEffect(() => {
        setRowSelection({});
    }, [data]);

    const selectedRows = useMemo(() => {
        if (!enableMultiSelect) {
            return [];
        }
        return (data || []).filter((row, index) => {
            const rowId = String(row.id ?? index);
            return !!rowSelection[rowId];
        });
    }, [data, enableMultiSelect, rowSelection]);

    useEffect(() => {
        const tableColumns = columns.map(col => {

            let cellAlign = col.align || 'left';
            let columnProperties = {
                accessorKey: col.fieldName,
                header: col.headerName,
                size: col.size,
                ...(col.minSize != null ? { minSize: col.minSize } : {}),
            };

            if (col.headerName === 'ACTION_MENU') {
                columnProperties = {
                    ...columnProperties,
                    header: 'Action',
                    Header: <span style={col.headerStyle}>Action</span>,
                    enableColumnActions: false,
                    size: col.size || 110,
                    Cell: ({ row }) => {
                        const isSelected = row.original?.isChecked !== false;
                        const iconSx = {
                            cursor: 'pointer',
                            color: 'white',
                            fontSize: '18px',
                            margin: '1px',
                            borderRadius: '4px',
                            padding: '1px',
                        };
                        return (
                            <span
                                style={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}
                                onClick={(event) => event.stopPropagation()}
                            >
                                <CheckCircleIcon
                                    titleAccess="Select"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onRowAction?.('select', row.original);
                                    }}
                                    sx={{
                                        ...iconSx,
                                        backgroundColor: isSelected ? 'success.dark' : 'primary.dark',
                                        opacity: isSelected ? 1 : 0.75,
                                    }}
                                />
                                <CancelIcon
                                    titleAccess="Unselect"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onRowAction?.('unselect', row.original);
                                    }}
                                    sx={{
                                        ...iconSx,
                                        backgroundColor: !isSelected ? 'warning.dark' : 'grey.600',
                                        opacity: !isSelected ? 1 : 0.75,
                                    }}
                                />
                                <DeleteForeverRoundedIcon
                                    titleAccess="Remove"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onRowAction?.('remove', row.original);
                                    }}
                                    sx={{
                                        ...iconSx,
                                        backgroundColor: 'error.dark',
                                    }}
                                />
                            </span>
                        );
                    },
                };
            } else if (col.headerName === 'ROW_ACTIONS') {
                if (enableMultiSelect) {
                    columnProperties = {
                        ...columnProperties,
                        header: 'Select',
                        Header: <span style={col.headerStyle}>Select</span>,
                        enableColumnActions: false,
                        Cell: ({ row }) => {
                            const checked = !!rowSelection[row.id];
                            return (
                                <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(event) => {
                                        event.stopPropagation();
                                        const nextChecked = event.target.checked;
                                        const rowData = row.original;
                                        setRowSelection((current) => {
                                            const selectedList = (data || []).filter((item, index) => {
                                                const key = String(item.id ?? index);
                                                return !!current[key] && key !== row.id;
                                            });

                                            if (nextChecked && selectionGroupField && selectedList.length) {
                                                const groupValue = rowData?.[selectionGroupField];
                                                const mismatch = selectedList.some(
                                                    (item) => String(item?.[selectionGroupField] ?? '') !== String(groupValue ?? '')
                                                );
                                                if (mismatch) {
                                                    Swal.fire(
                                                        'Same grade only',
                                                        `Select deliveries with the same ${selectionGroupLabel}.`,
                                                        'warning'
                                                    );
                                                    return current;
                                                }
                                            }

                                            const next = { ...current };
                                            if (nextChecked) {
                                                next[row.id] = true;
                                            } else {
                                                delete next[row.id];
                                            }
                                            return next;
                                        });
                                    }}
                                />
                            );
                        }
                    };
                } else {
                    columnProperties = {
                        ...columnProperties,
                        header: 'Action',
                        Header: <span style={col.headerStyle}>Action</span>,
                        Cell: ({ row }) => {
                            return (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                                    <CheckCircleIcon
                                        titleAccess="Select"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            event.preventDefault();
                                            onRowSubmit(row.original.id, event);
                                        }}
                                        sx={{
                                            cursor: 'pointer',
                                            color: 'white',
                                            fontSize: '20px',
                                            backgroundColor: 'primary.dark',
                                            margin: '1px',
                                            borderRadius: '12px',
                                            padding: '1px'
                                        }}
                                    />
                                    {onRowClose ? (
                                        <CancelIcon
                                            titleAccess="Close - Not Required"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                event.preventDefault();
                                                onRowClose(row.original.id, event);
                                            }}
                                            sx={{
                                                cursor: 'pointer',
                                                color: 'white',
                                                fontSize: '20px',
                                                backgroundColor: row.original?.suggest_close ? '#b45309' : '#64748b',
                                                margin: '1px',
                                                borderRadius: '12px',
                                                padding: '1px'
                                            }}
                                        />
                                    ) : null}
                                </span>
                            );
                        }
                    };
                }
            } 
            else {

                if (col.headerStyle) {
                    columnProperties = {
                        ...columnProperties,
                        Header: <span style={col.headerStyle}>{col.headerName}</span>
                    };
                }

                columnProperties = {
                    ...columnProperties,
                    ...(col.wrap ? {
                        muiTableBodyCellProps: {
                            sx: {
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                verticalAlign: 'top',
                            },
                        },
                    } : {}),
                    Cell: ({renderedCellValue, row}) => {
                        const dynamicStyle = col.getCellStyle ? col.getCellStyle(row.original) : undefined;
                        const combinedStyle = (col.dataStyle || dynamicStyle)
                            ? { ...col.dataStyle, ...dynamicStyle }
                            : undefined;
                        const cellText = renderedCellValue ?? '';
                        const layoutStyle = col.wrap
                            ? {
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                lineHeight: 1.25,
                                overflow: 'hidden',
                            }
                            : {
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            };

                        return (
                            <div style={{ textAlign: cellAlign, ...layoutStyle }} title={String(cellText)}>
                                {combinedStyle ? <span style={combinedStyle}>{cellText}</span> : cellText}
                            </div>
                        );
                    }
                };
            }

            return columnProperties;
        });

        setTableColumns(tableColumns);

    },[columns, onRowSubmit, onRowClose, onRowAction, enableMultiSelect, rowSelection, data, selectionGroupField, selectionGroupLabel]);

    const handleMultiSubmit = () => {
        if (!selectedRows.length) {
            Swal.fire('Select at least one delivery', '', 'info');
            return;
        }
        if (selectionGroupField) {
            const first = String(selectedRows[0]?.[selectionGroupField] ?? '');
            const mismatch = selectedRows.some(
                (row) => String(row?.[selectionGroupField] ?? '') !== first
            );
            if (mismatch) {
                Swal.fire(
                    'Same grade only',
                    `Selected rows must share the same ${selectionGroupLabel}.`,
                    'warning'
                );
                return;
            }
        }
        onMultiSubmit?.(selectedRows);
    };

    return (
        <div style={{paddingTop: "10px", paddingBottom: "10px"}}>
            <h1 style={{fontSize: '22px', color: 'darkblue', fontWeight: 'bold', display:'none'}}>{title}</h1>
            {enableMultiSelect ? (
                <div style={{marginBottom: 8, fontWeight: 600, color: '#1e3a8a'}}>
                    Selected: {selectedRows.length}
                    {selectionGroupField ? ` (same ${selectionGroupLabel} only)` : ''}
                </div>
            ) : null}
            <MaterialReactTable
                columns={tableColumns}
                data={data}
                getRowId={(originalRow, index) => String(originalRow.id ?? index)}
                enablePinning
                enableStickyHeader
                enableColumnResizing
                columnResizeMode="onEnd" 
                enableColumnActions={false}
                enableColumnFilter={false}
                enableGlobalFilter={true}
                enablePagination={true}
                enableSorting={true}
                enableBottomToolbar={true}
                enableTopToolbar={true}
                muiTableBodyRowProps={({ row }) => ({
                    hover: true,
                    onClick: enableMultiSelect
                        ? undefined
                        : (event) => {
                            // Ignore clicks on interactive controls inside the row
                            if (event.target.closest('button, a, input, svg, .MuiIconButton-root')) {
                                return;
                            }
                            onRowSubmit?.(row.original.id, event);
                        },
                    sx: compactRows
                        ? {
                            height: '32px !important',
                            minHeight: '32px !important',
                            cursor: enableMultiSelect ? 'default' : 'pointer',
                        }
                        : {
                            minHeight: '36px',
                            cursor: enableMultiSelect ? 'default' : 'pointer',
                        },
                })}
                enableDensityToggle={true}
                state={{isLoading: isLoading, rowSelection, density: 'compact'}}
                onRowSelectionChange={enableMultiSelect ? setRowSelection : undefined}
                muiTablePaperProps={{
                    elevation: 0,
                    sx: {
                      borderRadius: '0',
                      border: '1px solid #000000',
                    },
                  }}
                  muiTableBodyProps={{
                    sx: {
                      '& tr:nth-of-type(odd)': {
                        backgroundColor:'#bcbfc4',
                      },
                      '& td': {
                        py: '2px !important',
                        px: '6px !important',
                        fontSize: '12px',
                        lineHeight: 1.2,
                      },
                    },
                  }}
                muiTableHeadCellProps={{
                    sx: {
                      fontWeight: 'bold',
                      fontSize: '12px',
                      backgroundColor: '#6e8fec',
                      color:'white',
                      py: '4px !important',
                      px: '6px !important',
                      lineHeight: 1.15,
                    },
                  }}
                muiTableBodyCellProps={{
                    sx: {
                      fontSize: '12px',
                      py: '2px !important',
                      px: '6px !important',
                      lineHeight: 1.2,
                      overflow: 'hidden',
                    },
                  }}
                initialState={
                    {
                        density: "compact",
                        showGlobalFilter: true,
                        pagination: {pageIndex: 0, pageSize: initialPageSize},
                        columnPinning: {  left:['id']}
                    }
                }
                renderTopToolbarCustomActions={
                    ({table}) => {
                        if (enableExportToolbar) {
                            return (
                                <span>
                                    <Button color="primary" onClick={onClose}  variant="contained">CLOSE</Button>
                                    <Button color="primary" onClick={onCSVExport} startIcon={<FileDownloadRoundedIcon/>} variant="contained">CSV</Button>
                                    &nbsp;&nbsp;
                                    <Button color="primary" onClick={onPDFExport} startIcon={<FileDownloadRoundedIcon/>} variant="contained">PDF</Button>
                                </span>
                            );
                        }
                        if (enableMultiSelect) {
                            return (
                                <>
                                    <Button color="primary" onClick={onClose} variant="contained">CLOSE</Button>
                                    &nbsp;&nbsp;
                                    <Button
                                        color="success"
                                        onClick={handleMultiSubmit}
                                        variant="contained"
                                        disabled={!selectedRows.length}
                                    >
                                        SELECT ({selectedRows.length})
                                    </Button>
                                </>
                            );
                        }
                        return (<> <Button color="primary" onClick={onClose}  variant="contained">CLOSE</Button></>);
                    }
                }
                renderDetailPanel={ enableDetailPanel 
                    ? ({ row }) => (
                        <Box
                            sx={{
                                display: 'grid',
                                margin: 'auto',
                                gridTemplateColumns: '1fr 1fr',
                                width: '100%',
                            }}
                        >
                            <Typography>Grades: {row.original.grades}</Typography>
                            <Typography>Delivery modes: {row.original.delivery_modes}</Typography>
                        </Box>
                    ) 
                    : undefined
                }
            />
        </div>
    );
}

export default ReactTablePopUpSubmitWrapper;
